<template>
  <div>
    <AppDialog
      title="Edit assigned members"
      class="holiday-setup-dialog"
      :show="showDialog"
      :loading="loading"
      append-to-body
      position="centered"
      @close="closeAllDialogs"
    >
      <ValidationObserver ref="dialogObserver">
        <HolidayMembersSetupForm
          :holiday="holiday"
          :current-members="currentMembers"
          :organization-id="organizationId"
          :overlap-shift-count="overlapShiftCount"
          :members-reload="membersReload"
          :loading="loading"
          :organization-tz="organizationTz"
          :country-list="countryList"
          editing
          @holiday-updated="handleHolidayUpdated"
          @disable-save-button="handleDisableSaveButton"
          @members-updated="handleMembersUpdated"
        />
      </ValidationObserver>
      <template #footer>
        <div class="d-flex align-items-end gap-15 flex-row-reverse">
          <AppButton
            class="update"
            type="primary"
            :disabled="saveButtonDisabled"
            @click="update"
          >
            Save
          </AppButton>
          <AppButton @click="closeAllDialogs">Cancel</AppButton>
        </div>
      </template>
    </AppDialog>

    <ShiftOverlapConfirmationDialog
      :show="showShiftOverlapConfirmationDialog"
      :overlap-shift-count="overlapShiftCount"
      :saving="saving"
      @update-remove-shift-field="handleRemoveShiftField"
      @close="closeDialog"
    />
  </div>
</template>

<script>
import { HolidaysResource } from '~/resources';
import HolidayMembersSetupForm from '~/components/holidays/HolidayMembersSetupForm.vue';
import ShiftOverlapConfirmationDialog from '~/components/ShiftOverlapConfirmationDialog.vue';
import HolidayMixin from '~/mixins/holidays/HolidayMixin';

export default {
  name: 'HolidayMembersSetupDialog',
  components: {
    HolidayMembersSetupForm,
    ShiftOverlapConfirmationDialog,
  },
  mixins: [HolidayMixin],
  data: () => ({
    showDialog: false,
    membersListData: {},
    holiday: { members_added_via: '', membership_rules: { countries: [], employment_types: [] } },
    holidayId: null,
    overlapShiftCount: 0,
    overlapShiftError: false,
    showShiftOverlapConfirmationDialog: false,
    saving: false,
    loading: false,
    currentMembers: [],
    disableSaveButton: false,
    membersReload: false,
  }),
  methods: {
    async toggleDialog(holidayId) {
      try {
        this.loading = true;
        this.membersReload = false;
        this.showDialog = true;
        this.holidayId = holidayId;
        const params = { id: this.holidayId };
        const response = await HolidaysResource.edit(params, null, { abortReload: true });

        if (response.holiday) {
          this.holiday = {
            ...this.holiday,
            ...response.holiday,
            members_added_via: response.holiday.has_membership_rules ? 'country_and_employment' : 'list',
            remove_shifts: false,
          };

          this.currentMembers = response.holiday.holiday_user_ids;
          this.checkShiftOverlaps();
          this.loading = false;
        } else {
          throw new Error('Unable to fetch data for the holiday edition.');
        }
      } catch (error) {
        this.closeAllDialogs();
        await this.$appMessage('Unable to fetch data for the update holiday members. Try again later.');
      }
    },
    closeAllDialogs() {
      this.showDialog = false;
      this.showShiftOverlapConfirmationDialog = false;
      this.overlapShiftCount = 0;
      this.holiday = { members_added_via: '' };
      this.membersReload = true;
      this.loading = false;
      this.membersListData = {};
      this.disableSaveButton = false;
    },
    async handleRemoveShiftField(field, value) {
      this.handleHolidayUpdated(field, value);
      await this.update();
    },
    async update() {
      this.performHolidayAction('updateMembers');
    },
    async updateHolidayMembers() {
      try {
        const params = { id: this.holidayId };
        const holidayParams = {
          holiday: {
            remove_shifts: this.holiday.remove_shifts,
            holiday_user_ids: this.holiday.holiday_user_ids,
            auto_add_new_users: this.holiday.auto_add_new_users,
            members_added_via: this.holiday.members_added_via,
            membership_rules: this.holiday.membership_rules,
          },
          members_data: this.membersListData,
        };

        if (this.overlapShiftError) {
          holidayParams.overlap_shifts_error = this.overlapShiftError;
        }
        const response = await HolidaysResource.updateMembers(params, holidayParams, { abortReload: true });

        if (response.success) {
          this.closeAllDialogs();
          window.location.reload();
        } else if (response.errors) {
          this.$refs.dialogObserver.setErrors(response.errors);
          this.showShiftOverlapConfirmationDialog = false;
          this.showDialog = true;
          this.$appMessage('The holiday members cannot be updated.');
        } else {
          throw new Error('Unable to update holiday members');
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.showShiftOverlapConfirmationDialog = false;
          this.showDialog = true;
          this.$appMessage('Unexpected error. Please try again.');
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
